<template>
  <div
    ref="containerRef"
    class="game-container"
    :class="{ embedded, 'input-disabled': !inputEnabled }"
    tabindex="0"
    @click="onPrimaryInput"
    @keydown.space.prevent="onPrimaryInput"
  >
    <canvas ref="canvasRef" class="game-canvas"></canvas>

    <div v-if="phase === 'loading'" class="overlay loading-overlay">
      <p class="overlay-text">CARREGANDO...</p>
    </div>

    <div v-if="phase === 'gameover'" class="overlay gameover-overlay">
      <div class="gameover-score-wrap">
        <img :src="pontuacaoAsset" alt="Pontuacao" class="pontuacao-bg" />
        <canvas ref="gameOverScoreCanvasRef" class="gameover-score-canvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue'

import carroImg from '@/assets/games/carro.png'
import colunaImg from '@/assets/games/coluna-superior.png'
import trilhoNormalImg from '@/assets/games/trilho-normal.png'
import trilhoDescidaImg from '@/assets/games/trilho-descida.png'
import trilhoSubidaImg from '@/assets/games/trilho-subida.png'
import gramaImg from '@/assets/games/grama.png'
import arbustoImg from '@/assets/games/arbusto.png'
import nuvemImg from '@/assets/games/nuvem.png'
import montanhaImg from '@/assets/games/montanha.png'
import airtimeImg from '@/assets/games/airtime.png'
import toqueImg from '@/assets/games/toque.png'
import fonteImg from '@/assets/games/fonte.png'
import pontuacaoImg from '@/assets/games/pontuacao.png'

const props = defineProps({
  inputEnabled: {
    type: Boolean,
    default: true
  },
  embedded: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['assets-ready', 'game-active'])

const canvasRef = ref(null)
const containerRef = ref(null)
const gameOverScoreCanvasRef = ref(null)
const phase = ref('idle') // idle | loading | playing | gameover
const score = ref(0)
const pontuacaoAsset = pontuacaoImg

let ctx = null
let width = 0
let height = 0
let pixelRatio = 1
let rafId = 0
let idleRafId = 0
let lastFrame = 0
let assetsLoaded = false
let numberSprites = {}
let trophySprite = null
const hitboxHalfScale = 0.21
let lastMobileTapTs = 0
const doubleTapWindowMs = 320

const images = {
  car: null,
  pipeTop: null,
  pipeNormal: null,
  pipeDown: null,
  pipeUp: null,
  grass: null,
  bush: null,
  cloud: null,
  mountain: null,
  logo: null,
  touch: null,
  fonte: null
}

const world = {
  birdX: 0,
  birdY: 0,
  birdW: 96,
  birdH: 96,
  birdV: 0,
  gravity: 0.25,
  jump: -6,
  pipeWidth: 144,
  normalGap: 176,
  slopeGapMultiplier: 1.5,
  pipeSpeed: 3,
  pipes: [],
  grassX: 0,
  bushX: 0,
  cloudX: 0,
  mountainX: 0,
  floatT: 0,
  logoT: 0,
  touchT: 0
}

const sourceMap = {
  car: carroImg,
  pipeTop: colunaImg,
  pipeNormal: trilhoNormalImg,
  pipeDown: trilhoDescidaImg,
  pipeUp: trilhoSubidaImg,
  grass: gramaImg,
  bush: arbustoImg,
  cloud: nuvemImg,
  mountain: montanhaImg,
  logo: airtimeImg,
  touch: toqueImg,
  fonte: fonteImg
}

const resize = () => {
  width = window.innerWidth
  height = window.innerHeight
  const canvas = canvasRef.value
  if (!canvas || !ctx) return

  pixelRatio = Math.max(1, Math.round(window.devicePixelRatio || 1))
  canvas.width = Math.floor(width * pixelRatio)
  canvas.height = Math.floor(height * pixelRatio)
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  ctx.imageSmoothingEnabled = false
  world.birdX = width * 0.42
  if (phase.value !== 'playing') {
    world.birdY = height * 0.6
    drawIdleScreen()
  }
}

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image()
  img.decoding = 'async'
  img.onload = () => resolve(img)
  img.onerror = () => {
    img.error = true
    resolve(img)
  }
  img.src = src
})

const ensureAssets = async () => {
  if (assetsLoaded || phase.value === 'loading') return

  phase.value = 'loading'
  const entries = await Promise.all(
    Object.entries(sourceMap).map(async ([key, src]) => [key, await loadImage(src)])
  )

  for (const [key, img] of entries) images[key] = img
  if (images.fonte && images.fonte.complete && !images.fonte.error) {
    extractNumberSprites(images.fonte)
  }

  assetsLoaded = true
  emit('assets-ready')

  phase.value = 'idle'
  startIdleAnimation()
}

const extractNumberSprites = (fonte) => {
  const spriteWidth = Math.floor(fonte.width / 11)
  const spriteHeight = fonte.height
  const order = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]
  const tmp = document.createElement('canvas')
  const tctx = tmp.getContext('2d')
  tmp.width = spriteWidth
  tmp.height = spriteHeight

  numberSprites = {}

  for (let i = 0; i < 10; i++) {
    tctx.clearRect(0, 0, spriteWidth, spriteHeight)
    tctx.drawImage(fonte, i * spriteWidth, 0, spriteWidth, spriteHeight, 0, 0, spriteWidth, spriteHeight)
    const c = document.createElement('canvas')
    c.width = spriteWidth
    c.height = spriteHeight
    c.getContext('2d').drawImage(tmp, 0, 0)
    numberSprites[order[i]] = c
  }

  tctx.clearRect(0, 0, spriteWidth, spriteHeight)
  tctx.drawImage(fonte, 10 * spriteWidth, 0, spriteWidth, spriteHeight, 0, 0, spriteWidth, spriteHeight)
  trophySprite = document.createElement('canvas')
  trophySprite.width = spriteWidth
  trophySprite.height = spriteHeight
  trophySprite.getContext('2d').drawImage(tmp, 0, 0)
}

const randomPipeType = () => {
  const r = Math.random()
  if (r < 0.8) return 'normal'
  if (r < 0.9) return 'down'
  return 'up'
}

const addPipe = () => {
  const type = randomPipeType()
  const isSlope = type !== 'normal'
  const gap = isSlope ? world.normalGap * world.slopeGapMultiplier : world.normalGap
  const minHeight = 60
  const maxHeight = height - gap - 150
  const topHeight = Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight

  world.pipes.push({
    x: width + 120,
    topHeight,
    bottomY: topHeight + gap,
    type,
    scored: false
  })
}

const startGame = () => {
  cancelLoop()
  stopIdleAnimation()
  emit('game-active', true)

  phase.value = 'playing'
  score.value = 0
  world.pipes = []
  world.birdY = height * 0.35
  world.birdV = 0
  world.grassX = 0
  world.bushX = 0
  world.cloudX = 0
  world.mountainX = 0
  lastFrame = 0

  addPipe()
  loop(0)
}

const endGame = () => {
  phase.value = 'gameover'
  cancelLoop()
  stopIdleAnimation()
  emit('game-active', false)
  drawWorld(false)
  nextTick(() => drawGameOverScore())
}

const onPrimaryInput = (isTouch = false) => {
  if (!props.inputEnabled) return

  if (!assetsLoaded) {
    return
  }

  const needsDoubleTap = isTouch && window.matchMedia?.('(pointer: coarse)')?.matches

  if (phase.value === 'idle') {
    if (needsDoubleTap) {
      const now = performance.now()
      if (now - lastMobileTapTs > doubleTapWindowMs) {
        lastMobileTapTs = now
        return
      }
      lastMobileTapTs = 0
    }
    startGame()
    return
  }

  if (phase.value === 'playing') {
    world.birdV = world.jump
    return
  }

  if (phase.value === 'gameover') {
    if (needsDoubleTap) {
      const now = performance.now()
      if (now - lastMobileTapTs > doubleTapWindowMs) {
        lastMobileTapTs = now
        return
      }
      lastMobileTapTs = 0
    }
    startGame()
  }
}

const collisionWithPipe = (pipe) => {
  const halfW = world.birdW * hitboxHalfScale
  const halfH = world.birdH * hitboxHalfScale
  const birdLeft = world.birdX - halfW
  const birdRight = world.birdX + halfW
  const birdTop = world.birdY - halfH
  const birdBottom = world.birdY + halfH

  const pipeLeft = pipe.x
  const pipeRight = pipe.x + world.pipeWidth

  if (birdRight < pipeLeft || birdLeft > pipeRight) return false

  if (birdTop < pipe.topHeight) return true

  const bottomTop = pipe.bottomY

  if (pipe.type === 'normal') {
    return birdBottom > bottomTop + 96
  }

  const p = Math.max(0, Math.min(1, (world.birdX - pipeLeft) / world.pipeWidth))
  if (pipe.type === 'down') {
    return birdBottom > bottomTop + (96 + 120 * p)
  }

  return birdBottom > bottomTop + (216 - 120 * p)
}

const update = (dt) => {
  world.floatT += dt
  world.logoT += dt
  world.touchT += dt

  world.birdV += world.gravity * (dt * 60)
  world.birdY += world.birdV * (dt * 60)

  const grassH = images.grass?.complete && !images.grass.error ? images.grass.height * 2 : 96
  const floorY = height - grassH

  if (world.birdY + world.birdH * hitboxHalfScale >= floorY || world.birdY - world.birdH * hitboxHalfScale <= 0) {
    endGame()
    return
  }

  const speed = world.pipeSpeed * (dt * 60)
  world.grassX -= speed
  world.bushX -= speed * 0.6
  world.cloudX -= speed * 0.24
  world.mountainX -= speed * 0.36

  for (const pipe of world.pipes) {
    pipe.x -= speed

    if (!pipe.scored && pipe.x + world.pipeWidth * 0.5 < world.birdX) {
      pipe.scored = true
      score.value += 1
    }

    if (collisionWithPipe(pipe)) {
      endGame()
      return
    }
  }

  world.pipes = world.pipes.filter((p) => p.x + world.pipeWidth > -120)

  if (world.pipes.length === 0 || world.pipes[world.pipes.length - 1].x < width - 500) {
    addPipe()
  }
}

const drawLayerLoop = (img, xRef, y, scale = 2, spacingFactor = 1) => {
  if (!img || !img.complete || img.error) return
  const w = img.width * scale
  const h = img.height * scale
  const step = w * spacingFactor
  let x = xRef
  while (x < width + w) {
    ctx.drawImage(img, x, y, w, h)
    x += step
  }
  if (xRef <= -step) {
    return 0
  }
  return xRef
}

const drawTopColumn = (pipe) => {
  const img = images.pipeTop
  if (!img || !img.complete || img.error) return

  let remaining = pipe.topHeight
  let y = 0
  while (remaining > 0) {
    const h = Math.min(img.height, remaining)
    ctx.drawImage(img, 0, 0, world.pipeWidth, h, pipe.x, y, world.pipeWidth, h)
    remaining -= h
    y += h
  }
}

const drawBottomPipe = (pipe) => {
  const img = pipe.type === 'normal' ? images.pipeNormal : pipe.type === 'down' ? images.pipeDown : images.pipeUp
  if (!img || !img.complete || img.error) return
  const columnImg = images.pipeTop

  const grassH = images.grass?.complete && !images.grass.error ? images.grass.height * 2 : 96
  const floorY = height - grassH
  const available = floorY - pipe.bottomY

  let y = pipe.bottomY
  let remaining = available

  if (remaining > 0 && y < floorY) {
    const topRailH = Math.min(img.height, remaining)
    ctx.drawImage(img, 0, 0, world.pipeWidth, topRailH, pipe.x, y, world.pipeWidth, topRailH)
    y += topRailH
    remaining -= topRailH
  }

  if (columnImg && columnImg.complete && !columnImg.error) {
    while (remaining > 0 && y < floorY) {
      const h = Math.min(columnImg.height, remaining)
      ctx.drawImage(columnImg, 0, 0, world.pipeWidth, h, pipe.x, y, world.pipeWidth, h)
      y += h
      remaining -= h
    }
  }
}

const drawBird = () => {
  const img = images.car
  if (!img || !img.complete || img.error) {
    ctx.fillStyle = '#ff0000'
    ctx.fillRect(world.birdX - 20, world.birdY - 20, 40, 40)
    return
  }

  ctx.save()
  ctx.translate(world.birdX, world.birdY)
  const rot = Math.min(Math.max(world.birdV * 0.05, -0.5), 0.5)
  ctx.rotate(rot)

  const ratio = img.width / img.height
  let dw = world.birdW
  let dh = world.birdW / ratio
  if (dh > world.birdH) {
    dh = world.birdH
    dw = world.birdH * ratio
  }

  ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh)
  ctx.restore()
}

const drawStaticScene = (timeSeconds = 0) => {
  ctx.fillStyle = '#4A90E2'
  ctx.fillRect(0, 0, width, height)

  const grassH = images.grass?.complete && !images.grass.error ? images.grass.height * 2 : 96
  const floorY = height - grassH

  drawLayerLoop(images.cloud, 0, floorY - (images.cloud?.height || 0) * 2, 2, 1)
  drawLayerLoop(images.mountain, 0, floorY - (images.mountain?.height || 0) * 2, 2, 1.5)
  drawLayerLoop(images.bush, 0, floorY - (images.bush?.height || 0) * 2, 2, 1)
  drawLayerLoop(images.grass, 0, floorY, 2, 1)

  const compactLayout = !props.inputEnabled

  const carFloat = Math.sin(timeSeconds * 2) * (world.birdH * 0.1)
  const carRotDeg = Math.sin(timeSeconds * 2) * 15
  if (images.car?.complete && !images.car.error) {
    const ratio = images.car.width / images.car.height
    let dw = world.birdW
    let dh = world.birdW / ratio
    if (dh > world.birdH) {
      dh = world.birdH
      dw = world.birdH * ratio
    }
    ctx.save()
    const carY = compactLayout ? height * 0.52 : height * 0.6
    ctx.translate(world.birdX, carY + carFloat)
    ctx.rotate((carRotDeg * Math.PI) / 180)
    ctx.drawImage(images.car, -dw / 2, -dh / 2, dw, dh)
    ctx.restore()
  }

  if (images.logo?.complete && !images.logo.error) {
    const logoScale = 1 + Math.sin(timeSeconds * 2.2) * 0.1
    const logoRotDeg = Math.sin(timeSeconds * 2.2) * 15
    const w = images.logo.width * 3 * logoScale
    const h = images.logo.height * 3 * logoScale
    const cx = width / 2
    const cy = compactLayout ? height * 0.34 : height * 0.18
    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate((logoRotDeg * Math.PI) / 180)
    ctx.drawImage(images.logo, Math.round(-w / 2), Math.round(-h / 2), Math.round(w), Math.round(h))
    ctx.restore()
  }

  if (images.touch?.complete && !images.touch.error) {
    const w = images.touch.width * 2
    const h = images.touch.height * 2
    const touchBob = Math.sin(timeSeconds * 2.4) * (h * 0.1)
    const touchBaseY = compactLayout ? height * 0.68 : height * 0.8
    ctx.drawImage(images.touch, Math.round(width / 2 - w / 2), Math.round(touchBaseY - h / 2 + touchBob), w, h)
  }
}

const drawWorld = (showTopScore = true) => {
  ctx.fillStyle = '#4A90E2'
  ctx.fillRect(0, 0, width, height)

  const grassH = images.grass?.complete && !images.grass.error ? images.grass.height * 2 : 96
  const floorY = height - grassH

  world.cloudX = drawLayerLoop(images.cloud, world.cloudX, floorY - (images.cloud?.height || 0) * 2, 2, 1)
  world.mountainX = drawLayerLoop(images.mountain, world.mountainX, floorY - (images.mountain?.height || 0) * 2, 2, 1.5)
  world.bushX = drawLayerLoop(images.bush, world.bushX, floorY - (images.bush?.height || 0) * 2, 2, 1)

  for (const p of world.pipes) drawBottomPipe(p)
  drawBird()
  for (const p of world.pipes) drawTopColumn(p)

  world.grassX = drawLayerLoop(images.grass, world.grassX, floorY, 2, 1)

  if (showTopScore) {
    drawScoreWithSprite(ctx, score.value, width / 2, Math.round(height * 0.2), 2.5, false)
  }
}

const drawScoreWithSprite = (targetCtx, value, centerX, topY, scale = 2.5, withTrophy = false) => {
  const digits = String(value).split('').map((d) => Number(d))
  const firstSprite = numberSprites[0] || Object.values(numberSprites)[0]
  if (!firstSprite) return

  const sw = firstSprite.width
  const sh = firstSprite.height
  const dw = Math.round(sw * scale)
  const dh = Math.round(sh * scale)
  const spacing = Math.max(2, Math.round(scale * 2))
  const partsCount = digits.length + (withTrophy ? 1 : 0)
  const totalW = partsCount * dw + (partsCount - 1) * spacing
  let x = Math.round(centerX - totalW / 2)

  if (withTrophy && trophySprite) {
    targetCtx.drawImage(trophySprite, x, topY, dw, dh)
    x += dw + spacing
  }

  for (const d of digits) {
    const sprite = numberSprites[d]
    if (sprite) targetCtx.drawImage(sprite, x, topY, dw, dh)
    x += dw + spacing
  }
}

const drawGameOverScore = () => {
  const canvas = gameOverScoreCanvasRef.value
  if (!canvas) return
  const c = canvas.getContext('2d')
  if (!c) return

  const base = numberSprites[0] || Object.values(numberSprites)[0]
  if (!base) return

  const scale = 4
  const sw = base.width
  const sh = base.height
  const dw = Math.round(sw * scale)
  const dh = Math.round(sh * scale)
  const spacing = Math.max(6, Math.round(scale * 2))
  const count = String(score.value).length + 1
  const cssW = count * dw + (count - 1) * spacing
  const cssH = dh
  const dpr = Math.max(1, Math.round(window.devicePixelRatio || 1))

  canvas.width = cssW * dpr
  canvas.height = cssH * dpr
  canvas.style.width = `${cssW}px`
  canvas.style.height = `${cssH}px`
  c.setTransform(dpr, 0, 0, dpr, 0, 0)
  c.imageSmoothingEnabled = false
  c.clearRect(0, 0, cssW, cssH)
  drawScoreWithSprite(c, score.value, cssW / 2, 0, scale, true)
}

const drawIdleScreen = () => {
  if (!ctx) return
  if (assetsLoaded) {
    drawStaticScene(performance.now() / 1000)
  } else {
    ctx.fillStyle = '#4A90E2'
    ctx.fillRect(0, 0, width, height)
  }
}

const startIdleAnimation = () => {
  if (idleRafId || !assetsLoaded || phase.value !== 'idle') return
  const tick = (ts) => {
    if (phase.value !== 'idle') {
      idleRafId = 0
      return
    }
    drawStaticScene(ts / 1000)
    idleRafId = requestAnimationFrame(tick)
  }
  idleRafId = requestAnimationFrame(tick)
}

const stopIdleAnimation = () => {
  if (!idleRafId) return
  cancelAnimationFrame(idleRafId)
  idleRafId = 0
}

const loop = (ts) => {
  if (phase.value !== 'playing') return

  if (!lastFrame) lastFrame = ts
  const dt = Math.min(0.05, (ts - lastFrame) / 1000)
  lastFrame = ts

  update(dt)
  if (phase.value === 'playing') {
    drawWorld()
    rafId = requestAnimationFrame(loop)
  }
}

const cancelLoop = () => {
  if (!rafId) return
  cancelAnimationFrame(rafId)
  rafId = 0
}

const onTouchStart = (e) => {
  if (!props.inputEnabled) return
  e.preventDefault()
  e.stopPropagation()
  onPrimaryInput(true)
}

watch(() => props.inputEnabled, (enabled) => {
  if (!enabled) {
    cancelLoop()
    emit('game-active', false)
    if (phase.value === 'idle') {
      startIdleAnimation()
    }
    return
  }

  if (enabled && phase.value !== 'playing') {
    if (phase.value === 'idle') {
      startIdleAnimation()
    } else {
      drawIdleScreen()
    }
  }
})

onMounted(() => {
  const canvas = canvasRef.value
  if (!canvas) return

  ctx = canvas.getContext('2d', {
    alpha: false,
    imageSmoothingEnabled: false,
    desynchronized: true,
    willReadFrequently: false
  })

  resize()
  ensureAssets()

  window.addEventListener('resize', resize)
  canvas.addEventListener('touchstart', onTouchStart, { passive: false })
  canvas.addEventListener('touchend', (e) => {
    if (!props.inputEnabled) return
    e.preventDefault()
  }, { passive: false })
})

onBeforeUnmount(() => {
  cancelLoop()
  stopIdleAnimation()
  window.removeEventListener('resize', resize)
  if (canvasRef.value) {
    canvasRef.value.removeEventListener('touchstart', onTouchStart)
  }
})
</script>

<style scoped>
.game-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  cursor: pointer;
  outline: none;
  user-select: none;
  overflow: hidden;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
}

.game-container.embedded {
  position: absolute;
}

.game-container.embedded.input-disabled,
.game-container.embedded.input-disabled .game-canvas {
  touch-action: pan-y;
}

.game-canvas {
  display: block;
  width: 100%;
  height: 100%;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
}

.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 12;
  pointer-events: none;
}

.loading-overlay {
  background: rgba(0, 0, 0, 0.28);
}

.overlay-text {
  margin: 0;
  color: #ffffff;
  font-family: "Archivo", sans-serif;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.gameover-card {
  display: none;
}

.label,
.value,
.hint {
  margin: 0;
  color: #ffffff;
  text-align: center;
  font-family: "Archivo", sans-serif;
}

.label {
  font-size: 16px;
  opacity: 0.85;
}

.value {
  font-size: 38px;
  font-weight: 700;
  line-height: 1.2;
}

.gameover-overlay {
  flex-direction: column;
  gap: 0;
}

.gameover-score-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(2);
  transform-origin: center center;
}

.pontuacao-bg {
  width: auto;
  max-width: none;
  height: auto;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.gameover-score-canvas {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}
</style>
